import events from "_src/engine/events";
import output from "_src/engine/output";

import {vec3, mat4} from "_src/libs/gl-matrix";

import { stepType } from "_src/constants";
import Selector from "_src/client/manipulators/selector";
import selector from "_src/manipulators/libs/selector";
import smoother from "_src/manipulators/libs/smoother";
import workspace from "_src/workspace";
import widgets from "_src/client/widgets/selection_shapes";
import history from "_src/history/manager";
import windowManager from "_src/client/window_manager";
import config from "_src/config";

const _VEC3_TMP = vec3.create();

const _chunksStack = [];

export default class Smoother extends Selector {

  constructor(camera) {

    super(camera);

    this._strength = 1;

    this._storedSteps = [];

  }

  setStrength(strength) {
    this._strength = strength;
  }

  _startMoveHandler(screenX, screenY) {
    this._smoothHandler(screenX, screenY);
  }

  _mouseMoveHandler({screenX, screenY, active}) {

    if (!active) {
      this._moveSphereToCursor(screenX, screenY);
      return;
    }

    this._smoothHandler(screenX, screenY);
  }

  _stopMoveHandler() {

    if (this._storedSteps.length > 0 && this._newProcessedObjects.size > 0) {
      const smoothedObjects = [];
      for (const changedObject of this._newProcessedObjects) {
        smoothedObjects.push({
          id: changedObject.id,
          localMatrix: mat4.clone(changedObject.model.localMatrix),
        });
      }

      history.addStep(stepType.SMOOTH);
      events.emit("objects_updated", this._newProcessedObjects);
      events.emit("smooth", {
        steps: this._storedSteps.slice(),
        objects: smoothedObjects,
      });

    }

    if (config.enableManipulatorsWarnings && this._newProcessedObjects.size === 0) {
      if (this._storedSteps.length > 0) {
        output.warn("Nothing smoothed:\n" + workspace.getVerbosityInfo());
      } else {
        output.warn("Nothing smoothed: no smooth steps");
      }
    }

    this._storedSteps.length = 0;
    this._newProcessedObjects.clear();

  }

  _mouseDownHandler({screenX, screenY}) {
    this._startMoveHandler(screenX, screenY);
  }

  _mouseUpHandler() {
    this._stopMoveHandler();
  }

  _onStart() {
    widgets.setSphereVisibility(true);

    this._storedSteps.length = 0;
  }

  _onStop() {
    widgets.setSphereVisibility(false);
  }

  _smoothHandler(screenX, screenY) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const forceRaycast = false;
    const forceRead = true;
    mainWindow.getPixelWorldPoint(screenX, screenY, this._camera, _VEC3_TMP, forceRaycast, forceRead).then((hit) => {

      let sphereCenter = _VEC3_TMP;

      if (!hit) {
        widgets.getSphereLocation(sphereCenter);
        this.recalcPointXY(screenX, screenY, sphereCenter, sphereCenter);
      }

      widgets.setSphereLocation(sphereCenter);
      widgets.updateSphereRadius();

      const objects = workspace.getObjects().filter(object => workspace.checkIsReadyToSelect(object));
      const sphereRadius = widgets.getSphereRadius();

      for (let object of objects) {
        _chunksStack.length = 0;

        if (!workspace.checkIsReadyToSmooth(object)) {
          continue;
        }

        const selected = selector.selectSphere(object, sphereCenter, sphereRadius);

        if (!selected) {
          continue;
        }

        // NOTE: we assume we deal with fusions here
        const frame = object.frames[0];
        const model = frame.model;
        const chunks = model.chunks;

        const selectedChunksIDs = [];

        for (let chunk of chunks) {

          const geometry = chunk.geometry;

          if (!geometry.checkHasMarkedVertices()) {
            continue;
          }

          selectedChunksIDs.push(chunk.getID());

          this._newProcessedObjects.add(object);

          _chunksStack.push(chunk);

        }

        if (selectedChunksIDs.length > 0) {
          history.writeFrameToCurrentStep({type: stepType.SMOOTH, frame: frame, chunksUp: selectedChunksIDs});
        }

        events.emit("shallow_object_update", object);
        mainWindow.renderGraph.requestFrame();

        smoother.smooth(object, _chunksStack, this._strength);
      }

      this._storedSteps.push( {position: vec3.clone(sphereCenter), radius: sphereRadius, strength: this._strength} );
    });

  }

}


